"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"

import { cn } from "@/lib/utils"

/**
 * MorphingHeader — sticky header that morphs on scroll.
 *
 * Two visual states keyed off `data-scrolled="true|false"` :
 *   - top of page : full width, transparent, flush with the viewport
 *   - scrolled    : visibly smaller pill (max-w-3xl), glass background,
 *                   lifted 8px, tighter padding
 *
 * 12 principles compliance :
 *   - timing-under-300ms : transitions = 250ms (under cap)
 *   - timing-consistent  : every animated property uses 250ms
 *   - easing-entrance-ease-out : cubic-bezier(0.16, 1, 0.3, 1) — quick
 *     arrival then gentle settle when the page scrolls past threshold
 *   - physics-active-state : Brand + NavLink keep `transition-colors` /
 *     `hover:opacity` for tactile feedback
 *   - physics-subtle-deformation : scale stays in 0.95-1 range never
 *     applied to text (would blur antialiasing) — we morph max-width
 *     and padding instead
 *   - staging-z-index-hierarchy : header at z-50, mobile sheet at z-60
 *
 * Performance choices :
 *   - Passive `scroll` listener throttled by `requestAnimationFrame`
 *     that only `setState`s when the threshold flips (≈ 2 setState
 *     for a full page scroll vs 200+ with motion's useScroll).
 *   - All animations are CSS transitions on a `data-scrolled` attribute :
 *     the browser compositor handles them off the main thread.
 *   - The header is `position: fixed` so changing its `max-width` /
 *     `padding` does NOT reflow the page below — only the header's
 *     internal content reflows, which is cheap.
 *   - 3-column grid (`auto 1fr auto`) so brand / nav / trailing don't
 *     fight for space when the pill shrinks.
 *
 * Compound API :
 *   <MorphingHeader>
 *     <MorphingHeader.Brand text="ADIS 95" href="/" logo={<Logo />} />
 *     <MorphingHeader.Nav>
 *       <MorphingHeader.NavLink href="/affutage">Affûtage</MorphingHeader.NavLink>
 *     </MorphingHeader.Nav>
 *   </MorphingHeader>
 *
 * Spacer :
 *   The header is fixed-position and floats above the rest of the page.
 *   To keep page content from sliding under the header on first paint,
 *   render `<MorphingHeader.Spacer />` once at the top of `<main>`.
 *   It reserves the resting (top-of-page) header height in normal flow.
 */

type MorphingContextValue = {
  scrolled: boolean
}

const MorphingContext = React.createContext<MorphingContextValue>({
  scrolled: false,
})

function useMorphing() {
  return React.useContext(MorphingContext)
}

/** Passive scroll listener throttled by requestAnimationFrame. */
function useScrolledPast(threshold: number): boolean {
  const [scrolled, setScrolled] = React.useState(false)
  const stateRef = React.useRef(false)

  React.useEffect(() => {
    let frame = 0
    const update = () => {
      frame = 0
      const next = window.scrollY > threshold
      // Only fire setState when the boolean truly flips. Most scroll
      // events don't cross the threshold so the React tree stays idle.
      if (next !== stateRef.current) {
        stateRef.current = next
        setScrolled(next)
      }
    }
    const onScroll = () => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(update)
      }
    }

    // Run once on mount in case the page loads scrolled (back-forward
    // restore, anchor jump, …).
    update()

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (frame !== 0) window.cancelAnimationFrame(frame)
    }
  }, [threshold])

  return scrolled
}

type MorphingHeaderProps = React.HTMLAttributes<HTMLElement> & {
  /** Pixels of scroll before the header morphs to its compact state. */
  threshold?: number
  /** Optional slot rendered to the right of the nav (CTA buttons, etc). */
  trailing?: React.ReactNode
}

function MorphingHeaderRoot({
  children,
  className,
  threshold = 24,
  trailing,
  ...rest
}: MorphingHeaderProps) {
  const scrolled = useScrolledPast(threshold)

  return (
    <MorphingContext.Provider value={{ scrolled }}>
      <header
        data-scrolled={scrolled ? "true" : "false"}
        className={cn(
          "pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center",
          className,
        )}
        {...rest}
      >
        <div
          data-scrolled={scrolled ? "true" : "false"}
          className={cn(
            // ── Layout : grille 3-cols stable. ON N'ANIME PLUS la
            // largeur ni le gap entre les colonnes — le contenu
            // reste à sa place, seul l'habillage visuel change.
            // Ça enlève l'effet "buggy / espacé" qu'on avait avec
            // max-width + gap qui s'animaient en parallèle.
            "pointer-events-auto relative w-[calc(100%-1rem)] max-w-7xl",
            "grid grid-cols-[auto_1fr_auto] items-center gap-3 md:gap-4",
            "px-4 py-3 md:px-5",
            // ── Visuel : seules les propriétés *paint* +
            // *transform* s'animent. Pas de reflow.
            "rounded-2xl border border-transparent bg-background/0",
            // Single transition declaration — toutes les propriétés
            // sur la même timeline 350ms ease-out. Plus court que
            // 450ms (qui sentait le ralenti) et pile dans la fenêtre
            // recommandée pour un morph scroll-driven.
            "transition-[transform,background-color,border-color,box-shadow,backdrop-filter,border-radius]",
            "duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
            "will-change-transform",
            // Scrolled state — la pill se contracte via transform: scale
            // (GPU, zero reflow). 0.94 (-6 %) était imperceptible →
            // 0.86 (-14 %) donne un changement de taille clair sans
            // sembler exagéré. Lift 8 px + glass chrome + rounded-full
            // complètent le morph. Origine top-center : la contraction
            // se fait également à gauche/droite, l'élément ne « saute »
            // pas vers un côté.
            "origin-[50%_0%]",
            "data-[scrolled=true]:scale-[0.86]",
            "data-[scrolled=true]:translate-y-2",
            "data-[scrolled=true]:rounded-full",
            "data-[scrolled=true]:border-border/40",
            "data-[scrolled=true]:bg-background/70",
            "data-[scrolled=true]:shadow-[0_8px_30px_-12px_oklch(0.16_0.008_75/0.18)]",
            "data-[scrolled=true]:backdrop-blur-2xl",
            "supports-[backdrop-filter]:data-[scrolled=true]:bg-background/55",
          )}
        >
          {children}
          {trailing ? (
            <div className="col-start-3 flex items-center justify-end gap-1 md:gap-1.5">
              {trailing}
            </div>
          ) : (
            // Spacer in column 3 so the Nav stays optically centred
            // even when no trailing slot is provided.
            <div aria-hidden="true" />
          )}
        </div>
      </header>
    </MorphingContext.Provider>
  )
}

type BrandProps = {
  text: string
  href?: string
  /** Optional logo node (img/svg) rendered before the wordmark. */
  logo?: React.ReactNode
  className?: string
}

function Brand({ text, href = "/", logo, className }: BrandProps) {
  return (
    <Link
      href={href}
      // `aria-label` garantit que le mot-marqué reste accessible aux
      // lecteurs d'écran même quand le span visible est caché en
      // scrolled state.
      aria-label={text}
      className={cn(
        // col-start-1 lock so DOM order changes never bump the brand
        // out of the leading slot.
        "col-start-1 flex shrink-0 items-center gap-2 text-foreground",
        "transition-opacity duration-200 hover:opacity-80",
        className,
      )}
    >
      {logo}
      {/* Wordmark : fade-out only, sans clip de largeur.
       *
       *  La version précédente clippait `max-w-0` quand scrolled →
       *  la colonne 1 du grid (auto) rétrécissait → la colonne 2
       *  (1fr Nav) s'étendait → les liens nav glissaient à gauche.
       *  Effet « text position move » signalé par l'utilisateur.
       *
       *  Maintenant : seul l'opacity descend à 0. Le wordmark devient
       *  invisible mais conserve sa largeur dans le flux → la grille
       *  ne se réajuste pas, le Nav reste pile au même pixel. Le
       *  scale(0.86) global fait pareil pour TOUT le contenu de la
       *  pill, y compris l'espace fantôme du wordmark — l'oeil voit
       *  juste un logo + une nav qui contractent symétriquement. */}
      <span
        className={cn(
          "whitespace-nowrap text-base font-extrabold tracking-tight",
          "transition-opacity duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
          "opacity-100 [[data-scrolled=true]_&]:opacity-0",
        )}
        aria-hidden="true"
      >
        {text}
      </span>
    </Link>
  )
}

type NavProps = {
  children: React.ReactNode
  className?: string
  "aria-label"?: string
}

function Nav({ children, className, "aria-label": ariaLabel }: NavProps) {
  return (
    <nav
      aria-label={ariaLabel ?? "Navigation principale"}
      className={cn(
        // Centre column. justify-self defaults to stretch on grid items
        // — `justify-center` on a flex container handles the visual
        // centring of the nav links inside their column.
        "col-start-2 hidden items-center justify-center gap-1 md:flex",
        // Tighter gap on scrolled state for visual coherence with the
        // smaller pill.
        "data-[scrolled=true]:gap-0.5 [[data-scrolled=true]_&]:gap-0.5",
        className,
      )}
    >
      {children}
    </nav>
  )
}

type NavLinkProps = {
  href: string
  /** Kept for API compatibility (initial entrance stagger). Currently
   *  ignored — the perf-tuned implementation skips per-link motion. */
  index?: number
  children: React.ReactNode
  className?: string
  /** When true (or href is "/"), match pathname strictly. */
  exact?: boolean
}

function NavLink({
  href,
  children,
  className,
  exact = false,
}: NavLinkProps) {
  const pathname = usePathname()
  const active =
    exact || href === "/"
      ? pathname === href
      : pathname?.startsWith(href) ?? false

  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative inline-flex min-h-[40px] items-center whitespace-nowrap rounded-full text-sm font-medium",
        // Tactile feedback — `physics-active-state` rule + subtle scale
        // (0.97, within the 0.95-1 range allowed by physics-subtle-
        // deformation).
        "active:scale-[0.97]",
        // Hover/active : transition rapide (200ms) pour la sensation
        // tactile click-feedback. Le padding du NavLink reste constant
        // (pas de morph) — c'est la pill globale qui rétrécit autour.
        "transition-[color,background-color,transform] duration-200",
        // Padding fixe (resting + scrolled identiques) → évite un
        // décalage de timing avec la morph globale (450ms).
        "px-3 py-1.5",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      {children}
    </Link>
  )
}

/**
 * Spacer component — render once at the top of `<main>` to reserve
 * vertical space for the fixed header so page content never hovers
 * under the carousel / hero images on first paint.
 *
 * Resting header height ≈ 4.75rem (76px : 8px outer breathing + 12px py
 * + 40px nav row + 12px py + 4px shadow allowance). We round to 5rem
 * mobile and 5.5rem desktop to leave a comfortable 8-12px gap between
 * the pill bottom and the first visible image.
 */
function Spacer({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn("h-20 md:h-24", className)}
      data-morphing-header-spacer
    />
  )
}

// Compound exports.
export const MorphingHeader = Object.assign(MorphingHeaderRoot, {
  Brand,
  Nav,
  NavLink,
  Spacer,
})

export { useMorphing }
export type {
  BrandProps as MorphingHeaderBrandProps,
  NavLinkProps as MorphingHeaderNavLinkProps,
}
